<template>
  <div>
    <div class="flex flex-between">
      <div>
        <slot name="title"/>
      </div>
      <div class="flex flex-right">
        <div
          v-if="useImportXLS && $can('show', 'allowGroupLotsXlsImportExport')"
          class="my-auto">
          <ImportGroupXLS
            v-if="tender"
            :disabled="imported"
            only_one
            :tender="tender"
            @finishImportLots="imported = false"
            @onSubmitTender="importSubmit"
            @startImportLots="imported = true"/>
        </div>
      </div>
    </div>
    <Groups
      can_expand
      class="pt-4 groups"
      content_class="pt-2 pb-2"
      group-key="guid"
      :groups="lotGroups"
      items-key="lots"
      small
      with_title>
      <template #title="{ group, index: groupIndex }">
        <LotGroupTitle
          :data-qa="`item-${groupIndex}-name`"
          :description="group.description"
          :title="`#${groupIndex + 1} ${group.title}`"/>
      </template>
      <template #after="{ group, index: groupIndex }">
        <div
          v-if="group.totalPrice"
          class="pr-6"> 
          <KeyValue
            :caption="$t('Tenders.TotalPrice')"
            :data-qa="`item-${groupIndex}-total-price`"
            :value="`${$n(group.totalPrice)} ${currency}`"/>
        </div>
      </template>
      <template #content="{ group, index: groupIndex }">
        <RowsTable
          :key="`group-lots-${groupIndex}`"
          :ref="`rowsTable_${group.id || 'default'}`"
          :change-field="checkValid"
          :disabled="imported"
          :headers="headers"
          :is-use-additional-fields="false"
          new-design
          :positions="group.lots"
          :selected="groupSelected[group.guid]"
          show-select
          :tender.sync="tenderMutable"
          :validate-prop-prefix="`lotGroups[${groupIndex}].lots`"
          @cancelSelection="value => handleGroupLotDelete(group, value)"
          @copyItem="value => handleGroupLotCopy(group, value)"
          @no-data-click="() => addPosition(group)"
          @update:positions="newLots => setGroupPositions(group, newLots)"
          @update:selected="val => setGroupSelected(group, val)"/>
        <div
          v-if="!tender.createdByExternalApi"
          class="text-left flex">
          <EditLotGroupProps
            :disabled="imported"
            :group="group"
            :rules="getLotGroupRules(that)"
            @update:group="val => editGroupLot(val, groupIndex)"/>
          <sButton
            data-qa="btn-added-lot"
            :disabled="imported"
            icon="delete"
            type="text"
            @click="deleteGroupLot(group)">
            {{ `${$t('Purchasing.Remove')} ${$t('Tenders.Lot')}` }}
          </sButton>
          <sDivider
            class="my-2"
            vertical/>
          <sButton
            data-qa="btn-added-lot"
            :disabled="imported"
            icon="plus"
            type="text"
            @click="addPosition(group)">
            {{ $t('Tenders.AddPosition') }}
          </sButton>
          <sButton
            data-qa="btn-added-lot"
            :disabled="!groupSelected[group.guid] || !groupSelected[group.guid].length || imported"
            icon="delete"
            type="text"
            @click="deleteSelectedPosition(group)">
            {{ $t('Tenders.RemovePosition') }}
          </sButton>
        </div>
      </template>
    </Groups>
    <div
      v-if="!tender.createdByExternalApi"
      class="mt-4 text-left">
      <sButton
        data-qa="btn-added-group"
        :disabled="imported"
        icon="plus"
        type="text"
        @click="addGroup">
        {{ $t('Tenders.ToAddLot') }}
      </sButton>
    </div>
  </div>
</template>

<script>
import KeyValue from 'components/common/KeyValue.vue'
import RowsTable from 'components/common/blocks/RowsTable.vue'
import Groups from 'components/common/blocks/groups/Groups.vue'
import ImportGroupXLS from 'components/tenders/dialogs/ImportXLS/ImportGroupXLS.vue'
import LotGroupTitle from 'components/tenders/tender page/tender tabs/tables/LotGroupTitle.vue'
import EditLotGroupProps from 'components/tenderCreation/block/EditLotGroupProps.vue'

import tenderLotsBaseMixin from 'mixins/tenderLotsBaseMixin.js'
import { getLotGroupRules } from 'components/tenderCreation/rules.js'
import { measureMixin, modalMixin } from 'mixins'

export default {
  name: 'EditLotGroup',
  mixins: [tenderLotsBaseMixin, measureMixin, modalMixin],
  data() {
    return {
      groupSelected: {},
      currencies: [],
    }
  },
  methods: {
    importSubmit(tenderModel) {
      if (this.$_initializeLotGroups) {
        this.$_initializeLotGroups(tenderModel, true)
      }
      this.tenderMutable = tenderModel
      this.updateRulesAfterChangeLots()
    },
    getLotGroupRules(index) {
      return getLotGroupRules(index)
    },
    setGroupSelected(group, lotNumbers) {
      this.$set(this.groupSelected, group.guid, lotNumbers)
    },
    setGroupPositions(groupItem, newLots) {
      const updatedTender = this.$_.cloneDeep(this.tenderMutable)
      updatedTender.lotGroups.find(i => i.guid === groupItem.guid).lots = newLots

      this.tenderMutable = updatedTender
      this.updateRulesAfterChangeLots()
    },
    handleGroupLotCopy(groupItem, value) {
      const oldLot = this.$_.cloneDeep(value)
      let newLot = this.createLotTemplate(this.getMaxLotNumberByGroup(groupItem) + 1)
      
      newLot.nomenclatureId = oldLot.nomenclatureId
      newLot.measuringUnit = oldLot.measuringUnit
      newLot.lotSubject = oldLot.lotSubject
      newLot.description = oldLot.description
      
      this.mixin_setAdditionalFieldsToLot(newLot)
      groupItem.lots = [...groupItem.lots, newLot]
      this.updateRulesAfterChangeLots()
    },
    /**
     * Получение максимального номера лота в разрезе одной группы
     */
    getMaxLotNumberByGroup(groupItem) {
      if (!groupItem.lots?.length) {
        return 0
      }
      return Math.max(...groupItem.lots?.map(i => i.lotNumber) ?? 0)
    },
    deleteSelectedPosition(groupItem) {
      if (!this.groupSelected[groupItem?.guid]?.length) {
        return
      }

      const group = this.tenderMutable.lotGroups?.find(g => g.guid === groupItem.guid) ?? {}
      const guids = group.lots
        ?.filter((item, index) => this.groupSelected[groupItem?.guid]
          ?.includes(index + 1 ))
        ?.map(i => i.guid) ?? []

      if (!guids.length) {
        this.$set(this.groupSelected, groupItem.guid, [])
        return
      }

      this.deletePositionsByGuids(groupItem, guids)
      this.$set(this.groupSelected, groupItem.guid, [])
      this.updateRulesAfterChangeLots()
    },
    deletePositionsByGuids(groupItem, guids) {
      const updatedTender = this.$_.cloneDeep(this.tenderMutable)
      const group = updatedTender.lotGroups?.find(g => g.guid === groupItem.guid)
      group.lots = groupItem.lots.filter(lot => !guids.includes(lot.guid))

      group.lots = this.renameLotsNumberInGroup(group.lots)

      this.tenderMutable = updatedTender
      this.$set(this.groupSelected, groupItem.guid, [])
      this.updateRulesAfterChangeLots()
    },
    handleGroupLotDelete(groupItem, value) {
      this.deletePositionsByGuids(groupItem, [value.guid])
      this.updateRulesAfterChangeLots()
    },
    renameLotsNumberInGroup(lots) {
      lots?.forEach((lot, index) => {
        lot.lotNumber = index + 1
      })
      return lots
    },
    editGroupLot(groupItem, groupIndex) {
      const updatedTender = this.$_.cloneDeep(this.tenderMutable)
      updatedTender.lotGroups[groupIndex] = groupItem
      this.tenderMutable = updatedTender

      this.sendEventRules(true)
    },
    /**
     * Удаляем лот
     */
    deleteGroupLot(groupItem) {
      const functionOk = () => {
        const updatedTender = this.$_.cloneDeep(this.tenderMutable)
        
        if (groupItem.guid) {
          updatedTender.lotGroups = updatedTender.lotGroups?.filter(g => g.guid !== groupItem.guid)
        }

        if (groupItem.lots?.length) {
          updatedTender.lots = updatedTender.lots.filter(lot => !groupItem.lots.some(groupLot => groupLot.guid === lot.guid))
        }

        this.tenderMutable = updatedTender
        
        this.updateRulesAfterChangeLots()
      }
      
      this.$Modal.confirm({
        title: `${this.$t('Tenders.SureDeleteLot')}?`,
        content: this.$t('Tenders.SureDeleteLotContent'),
        closable: true,
        okText: this.$t('Purchasing.Remove'),
        cancelText: this.$t('Common.Close'),
        dangerouslyUseHTMLString: true,
        onOk: functionOk,
      })
    },
    addPosition(groupItem) {
      let newLot = this.createLotTemplate(this.getMaxLotNumberByGroup(groupItem) + 1)
      groupItem.lots.push(newLot)
      this.updateRulesAfterChangeLots()
    },
    // Метод для добавления новой группы
    addGroup() {
      const updatedTender = this.$_.cloneDeep(this.tenderMutable)

      const newGroup = this.$_createGroupTemplate()
      newGroup.guid = this.$_createGuid() ?? []
      updatedTender.lotGroups.push(newGroup)

      this.tenderMutable = updatedTender
      this.updateRulesAfterChangeLots()
    },
  },
  computed: {
    currency() {
      let curr = this.currencies?.find(el => el.designation == this.tenderMutable.tenderCurrency)
      return curr?.shortTitle || this.$_getShortName(curr?.title) ||  '–'
    },
    lotGroups() {
      return this.tenderMutable.lotGroups?.map(group => {
        const totalPrice = group.lots?.reduce((acc, lot) => acc + ((lot.quantity * lot.startAmount) || 0), 0)
        return {
          ...group,
          totalPrice
        }
      }) ?? []
    }
  },
  mounted() {
    if (!this.tenderMutable.lotGroups?.length) {
      this.addGroup()
    } else {
      this.updateRulesAfterChangeLots()
    }
  },
  async created() {
    if (this.tenderMutable) {
      await this.$store.dispatch('setCurrencies')
        .then(res => this.currencies = res)
    }
  },
  components: {
    Groups,
    KeyValue,
    RowsTable,
    LotGroupTitle,
    ImportGroupXLS,
    EditLotGroupProps,
  },
}
</script>